import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import apiService from '../../services/api';
import { Bus } from '../../types';
import { DateDisplay, DateInput, FormField, Input, Select } from '../../components/Forms';
import BusSeatSelection from '../../components/BusSeatSelection';
import './public-booking.css';
import { getApiUrl } from '../../config/environment';

const MECCA_HERO_IMAGE = `${process.env.PUBLIC_URL || ''}/kaaba-hero.jpg`;

type CompanionDraft = {
  name: string;
  idNumber: string;
  nationality: string;
  phone: string;
  birthDate: string;
  gender: 'male' | 'female';
};

const emptyCompanion = (): CompanionDraft => ({
  name: '',
  idNumber: '',
  nationality: 'سعودي',
  phone: '',
  birthDate: '',
  gender: 'male',
});

const tripTypeLabel = (t?: string) =>
  t === 'hajj' ? 'حج' : t === 'transport' ? 'نقل' : 'عمرة';

const BOOKING_STEPS = [
  { id: 1, label: 'الرحلة', short: 'رحلة' },
  { id: 2, label: 'الحافلة', short: 'حافلة' },
  { id: 3, label: 'المسافرون', short: 'مسافرون' },
  { id: 4, label: 'المقاعد', short: 'مقاعد' },
  { id: 5, label: 'التأكيد', short: 'تأكيد' },
] as const;

const PublicBookingPage: React.FC = () => {
  const { vendorId = '' } = useParams<{ vendorId: string }>();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedTripId, setSelectedTripId] = useState('');
  const [selectedBusId, setSelectedBusId] = useState('');
  const [showSeatSelection, setShowSeatSelection] = useState(true);
  const [selectedSeats, setSelectedSeats] = useState<{ [index: number]: string }>({});
  const [contact, setContact] = useState({
    name: '',
    idNumber: '',
    phone: '',
    birthDate: '',
    nationality: 'سعودي',
    gender: 'male' as 'male' | 'female',
    email: '',
    organization: '',
  });
  const [companions, setCompanions] = useState<CompanionDraft[]>([]);
  const [specialRequests, setSpecialRequests] = useState('');
  const [submitted, setSubmitted] = useState<{
    bookingNumber: string;
    tripNumber?: string;
    status?: string;
    contactName?: string;
    passengersCount?: number;
    seats?: string;
    busNumber?: string;
    routeLabel?: string;
  } | null>(null);
  const [formError, setFormError] = useState('');
  const [confirmReady, setConfirmReady] = useState(false);

  const genderOptions = [
    { value: 'male', label: 'ذكر' },
    { value: 'female', label: 'أنثى' },
  ];

  const { data: office, isLoading: officeLoading, error: officeError } = useQuery({
    queryKey: ['public-booking-office', vendorId],
    queryFn: () => apiService.getPublicBookingOffice(vendorId),
    enabled: !!vendorId,
  });

  const { data: tripsData, isLoading: tripsLoading } = useQuery({
    queryKey: ['public-booking-trips', vendorId],
    queryFn: () => apiService.getPublicBookingTrips(vendorId),
    enabled: !!vendorId && !!office,
  });

  const trips = tripsData?.data || [];

  const {
    data: tripDetail,
    isFetching: tripDetailFetching,
  } = useQuery({
    queryKey: ['public-booking-trip', vendorId, selectedTripId],
    queryFn: () => apiService.getPublicBookingTrip(vendorId, selectedTripId),
    enabled: !!vendorId && !!selectedTripId,
  });

  const selectedTrip = tripDetail || trips.find((t: any) => t._id === selectedTripId) || null;
  const buses = selectedTrip?.buses || [];

  useEffect(() => {
    if (!selectedTripId || !buses.length) return;
    const stillValid = buses.some(
      (b: any) => String(b.bus?._id) === String(selectedBusId)
    );
    if (!selectedBusId || !stillValid) {
      setSelectedBusId(String(buses[0].bus._id));
    }
  }, [selectedTripId, buses, selectedBusId]);

  const selectedBusEntry = useMemo(() => {
    if (!buses.length) return null;
    const found = buses.find((b: any) => String(b.bus?._id) === String(selectedBusId));
    return found || buses[0];
  }, [buses, selectedBusId]);

  const busForSeats: Bus | null = useMemo(() => {
    if (!selectedBusEntry?.bus) return null;
    const b = selectedBusEntry.bus;
    return {
      _id: b._id,
      busNumber: b.busNumber || '',
      plateNumber: b.plateNumber || '',
      capacity: b.capacity || 49,
      model: b.model,
      status: b.status || 'available',
      features: b.features || [],
      driver: b.driver || {
        name: '',
        phone: '',
        licenseNumber: '',
        licenseExpiry: new Date(),
      },
      supervisor: undefined,
      branch: { _id: '', name: '', code: '' } as any,
      maintenance: {},
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    } as Bus;
  }, [selectedBusEntry]);

  const extraReservedSeats: string[] = selectedBusEntry?.reservedSeats || [];

  const passengersForSeats = useMemo(() => {
    return [
      {
        name: contact.name.trim() || 'شخص التواصل',
        isMainContact: true,
      },
      ...companions.map((c, i) => ({
        name: c.name.trim() || `مرافق ${i + 1}`,
        isMainContact: false,
      })),
    ];
  }, [contact.name, companions]);

  const handleSeatSelect = (passengerIndex: number, seatNumber: string) => {
    setSelectedSeats((prev) => ({ ...prev, [passengerIndex]: seatNumber }));
  };

  const createMutation = useMutation({
    mutationFn: () =>
      apiService.createPublicBooking(vendorId, {
        tripId: selectedTripId,
        busId: selectedBusId || selectedBusEntry?.bus?._id,
        contactPerson: {
          ...contact,
          seatNumber: selectedSeats[0] || undefined,
        },
        companions: companions.map((c, i) => ({
          ...c,
          seatNumber: selectedSeats[i + 1] || undefined,
        })),
        specialRequests: specialRequests || undefined,
      }),
    onSuccess: (data) => {
      const routeInfo = selectedTrip?.routes?.[0];
      setSubmitted({
        bookingNumber: data.bookingNumber,
        tripNumber: data.tripNumber || selectedTrip?.tripNumber,
        status: data.status || 'pending',
        contactName: data.contactName || contact.name.trim(),
        passengersCount: 1 + companions.length,
        seats: Object.values(selectedSeats).filter(Boolean).join(' · ') || '',
        busNumber: selectedBusEntry?.bus?.busNumber || '',
        routeLabel:
          routeInfo?.from && routeInfo?.to
            ? `${routeInfo.from} ← ${routeInfo.to}`
            : '',
      });
      setFormError('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    onError: (err: any) => {
      setFormError(
        err?.response?.data?.message || err?.message || 'تعذر إرسال طلب الحجز'
      );
    },
  });

  const onSelectTrip = (id: string) => {
    setSelectedTripId(id);
    setSelectedSeats({});
    setShowSeatSelection(true);
    setFormError('');
    const trip = trips.find((t: any) => t._id === id);
    const firstBus = trip?.buses?.[0]?.bus?._id || '';
    setSelectedBusId(firstBus ? String(firstBus) : '');
  };

  const onSelectBus = (busId: string) => {
    setSelectedBusId(busId);
    setSelectedSeats({});
    setFormError('');
  };

  const validateStep = (step: number) => {
    if (step === 1) {
      if (!selectedTripId) return 'اختَر رحلة للمتابعة';
      return '';
    }
    if (step === 2) {
      if (!selectedBusId && !selectedBusEntry?.bus?._id) return 'اختَر حافلة للمتابعة';
      return '';
    }
    if (step === 3) {
      if (!contact.name.trim()) return 'اسم الشخص المسؤول مطلوب';
      if (!contact.idNumber.trim()) return 'رقم الهوية مطلوب';
      if (!contact.phone.trim()) return 'رقم الجوال مطلوب';
      if (!contact.birthDate) return 'تاريخ الميلاد مطلوب';
      if (!contact.nationality.trim()) return 'الجنسية مطلوبة';
      for (let i = 0; i < companions.length; i += 1) {
        const c = companions[i];
        if (!c.name.trim() || !c.idNumber.trim() || !c.birthDate || !c.nationality.trim()) {
          return `أكمل بيانات المرافق ${i + 1} (الاسم، الهوية، الجنسية، تاريخ الميلاد)`;
        }
      }
      return '';
    }
    if (step === 4) {
      const totalPeople = 1 + companions.length;
      for (let i = 0; i < totalPeople; i += 1) {
        if (!selectedSeats[i]) {
          return 'يجب اختيار مقعد لكل راكب من خريطة المقاعد';
        }
      }
      return '';
    }
    return '';
  };

  const validate = () => {
    for (let s = 1; s <= 4; s += 1) {
      const err = validateStep(s);
      if (err) return err;
    }
    return '';
  };

  const goNext = () => {
    const err = validateStep(currentStep);
    if (err) {
      setFormError(err);
      return;
    }
    setFormError('');
    setCurrentStep((s) => Math.min(5, s + 1));
  };

  const goBack = () => {
    setFormError('');
    setConfirmReady(false);
    setCurrentStep((s) => Math.max(1, s - 1));
  };

  const goToStep = (step: number) => {
    if (step < currentStep) {
      setFormError('');
      setConfirmReady(false);
      setCurrentStep(step);
    }
  };

  useEffect(() => {
    if (currentStep !== 5) {
      setConfirmReady(false);
      return;
    }
    setConfirmReady(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const timer = window.setTimeout(() => setConfirmReady(true), 900);
    return () => window.clearTimeout(timer);
  }, [currentStep]);

  const confirmBooking = () => {
    if (currentStep !== 5 || !confirmReady || createMutation.isPending) return;
    const err = validate();
    if (err) {
      setFormError(err);
      if (!selectedTripId) setCurrentStep(1);
      else if (!selectedBusId && !selectedBusEntry?.bus?._id) setCurrentStep(2);
      else if (validateStep(3)) setCurrentStep(3);
      else if (validateStep(4)) setCurrentStep(4);
      return;
    }
    setFormError('');
    createMutation.mutate();
  };

  const handleSubmit = (e: React.FormEvent) => {
    // منع الإرسال التلقائي (Enter أو ضغط غير مقصود)
    e.preventDefault();
  };

  const brandName = office?.companyName || office?.name || '';
  const getLogoUrl = (logo?: string | null) => {
    if (!logo) return null;
    if (logo.startsWith('http') || logo.startsWith('data:')) return logo;
    const apiUrl = getApiUrl();
    const base = apiUrl.replace(/\/api\/?$/, '');
    return `${base}${logo.startsWith('/') ? '' : '/'}${logo}`;
  };
  const logoUrl = getLogoUrl(office?.logo);
  const seatsChosen = Object.keys(selectedSeats).length;
  const seatsNeeded = 1 + companions.length;

  if (!vendorId) {
    return (
      <div className="public-booking-page min-h-screen flex items-center justify-center p-6" dir="rtl">
        <p className="text-gray-700">رابط الحجز غير مكتمل.</p>
      </div>
    );
  }

  if (officeLoading) {
    return (
      <div className="public-booking-page min-h-screen flex items-center justify-center" dir="rtl">
        <div className="h-10 w-10 rounded-full border-2 border-primary-200 border-t-primary-700 animate-spin" />
      </div>
    );
  }

  if (officeError || !office) {
    return (
      <div className="public-booking-page min-h-screen flex items-center justify-center p-6" dir="rtl">
        <div className="text-center space-y-3 max-w-md">
          <h1 className="text-xl font-bold text-gray-900">المكتب غير متاح</h1>
          <p className="text-sm text-gray-600">
            تعذر فتح رابط الحجز. تأكد من صحة الرابط أو تواصل مع المكتب.
          </p>
          <Link to="/" className="text-primary-700 text-sm underline">
            العودة للرئيسية
          </Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    const statusLabel =
      submitted.status === 'confirmed'
        ? 'مؤكد'
        : submitted.status === 'pending'
          ? 'قيد الانتظار'
          : submitted.status || 'قيد الانتظار';

    return (
      <div className="public-booking-page min-h-screen flex items-center justify-center p-6" dir="rtl">
        <div className="public-booking-section p-8 max-w-md w-full text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
            <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-xs text-gray-500 mb-1">{brandName}</p>
          <h1 className="text-xl font-bold text-gray-900 mb-2">تم تأكيد طلب الحجز</h1>
          <p className="text-sm text-gray-600 mb-5 leading-relaxed">
            استلمنا طلبك بنجاح. احتفظ برقم الحجز، وسيتواصل معك المكتب لإتمام التأكيد والدفع.
          </p>

          <div className="rounded-xl bg-slate-50 border border-slate-100 px-4 py-4 mb-4 text-right space-y-3">
            <div>
              <p className="text-xs text-gray-500 mb-1">رقم الحجز</p>
              <p className="text-xl font-bold text-primary-800 tracking-wide" dir="ltr">
                {submitted.bookingNumber}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm border-t border-slate-200/80 pt-3">
              <div>
                <p className="text-xs text-gray-500">الحالة</p>
                <p className="font-semibold text-amber-700">{statusLabel}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">المسافر المسؤول</p>
                <p className="font-medium text-slate-900">{submitted.contactName || '—'}</p>
              </div>
              {submitted.tripNumber && (
                <div>
                  <p className="text-xs text-gray-500">الرحلة</p>
                  <p className="font-medium text-slate-900">{submitted.tripNumber}</p>
                </div>
              )}
              {submitted.busNumber && (
                <div>
                  <p className="text-xs text-gray-500">الحافلة</p>
                  <p className="font-medium text-slate-900">{submitted.busNumber}</p>
                </div>
              )}
              {submitted.routeLabel && (
                <div className="col-span-2">
                  <p className="text-xs text-gray-500">المسار</p>
                  <p className="font-medium text-slate-900">{submitted.routeLabel}</p>
                </div>
              )}
              {submitted.passengersCount != null && (
                <div>
                  <p className="text-xs text-gray-500">عدد المسافرين</p>
                  <p className="font-medium text-slate-900">{submitted.passengersCount}</p>
                </div>
              )}
              {submitted.seats && (
                <div>
                  <p className="text-xs text-gray-500">المقاعد</p>
                  <p className="font-medium text-slate-900" dir="ltr">
                    {submitted.seats}
                  </p>
                </div>
              )}
            </div>
          </div>

          {office.contact?.phone && (
            <p className="text-sm text-gray-700 mb-5">
              للتواصل:{' '}
              <a href={`tel:${office.contact.phone}`} className="font-medium text-primary-800" dir="ltr">
                {office.contact.phone}
              </a>
            </p>
          )}
          <button
            type="button"
            className="w-full rounded-xl bg-primary-700 py-3 text-sm font-semibold text-white hover:bg-primary-800"
            onClick={() => {
              setSubmitted(null);
              setCurrentStep(1);
              setSelectedTripId('');
              setCompanions([]);
              setSelectedSeats({});
              setSpecialRequests('');
              setContact({
                name: '',
                idNumber: '',
                phone: '',
                birthDate: '',
                nationality: 'سعودي',
                gender: 'male',
                email: '',
                organization: '',
              });
            }}
          >
            حجز رحلة أخرى
          </button>
        </div>
      </div>
    );
  }

  const route = selectedTrip?.routes?.[0];

  return (
    <div className="public-booking-page" dir="rtl">
      <header className="public-booking-hero">
        <div className="public-booking-hero__media" aria-hidden="true">
          <img src={MECCA_HERO_IMAGE} alt="" />
        </div>
        <div className="public-booking-hero__veil" aria-hidden="true" />
        <div className="public-booking-hero__shimmer" aria-hidden="true" />

        <div className="public-booking-hero__content max-w-3xl mx-auto px-4 sm:px-5 pt-10 pb-12 sm:pt-14 sm:pb-16">
          <div className="public-booking-hero__brand flex items-center gap-4 sm:gap-5">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt=""
                className="h-16 w-16 sm:h-[4.75rem] sm:w-[4.75rem] rounded-2xl object-cover bg-white/15 ring-1 ring-white/25 shrink-0 shadow-lg shadow-black/20"
              />
            ) : (
              <div className="flex h-16 w-16 sm:h-[4.75rem] sm:w-[4.75rem] shrink-0 items-center justify-center rounded-2xl bg-white/12 ring-1 ring-white/25 text-2xl sm:text-3xl font-bold text-amber-200 shadow-lg shadow-black/20">
                {(brandName || 'ح').charAt(0)}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-amber-200/90 text-xs sm:text-sm font-semibold tracking-wide mb-1.5">
                حج وعمرة · حجز إلكتروني
              </p>
              <h1 className="public-booking-hero__title text-3xl sm:text-4xl font-extrabold leading-tight drop-shadow-sm">
                {brandName}
              </h1>
            </div>
          </div>

          <p className="public-booking-hero__sub mt-5 max-w-xl text-sm sm:text-base text-white/90 leading-relaxed">
            ابدأ رحلتك إلى بيت الله الحرام — اختَر الرحلة، عبّئ بيانات المسافرين، واحجز مقاعدك بخطوات بسيطة.
          </p>

          {(office.contact?.phone || office.contact?.email) && (
            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/75 border-t border-white/15 pt-4">
              {office.contact?.phone && (
                <a href={`tel:${office.contact.phone}`} className="hover:text-white transition-colors" dir="ltr">
                  {office.contact.phone}
                </a>
              )}
              {office.contact?.email && (
                <a href={`mailto:${office.contact.email}`} className="hover:text-white transition-colors" dir="ltr">
                  {office.contact.email}
                </a>
              )}
            </div>
          )}
        </div>
      </header>

      <main className={`max-w-3xl mx-auto px-4 sm:px-5 py-6 -mt-4 relative z-10 space-y-4 ${
        currentStep > 1 || selectedTripId ? 'pb-40' : 'pb-8'
      }`}>
        <div className="public-booking-section p-4 sm:p-5">
          <p className="text-xs font-semibold text-slate-500 mb-3">خطوات الحجز</p>
          <div className="public-booking-timeline">
            <div className="public-booking-timeline__track" aria-hidden="true">
              <div
                className="public-booking-timeline__progress"
                style={{
                  width: `${((currentStep - 1) / (BOOKING_STEPS.length - 1)) * 100}%`,
                }}
              />
            </div>
            {BOOKING_STEPS.map((step) => {
              const done = currentStep > step.id;
              const active = currentStep === step.id;
              const clickable = step.id < currentStep;
              return (
                <button
                  key={step.id}
                  type="button"
                  className={`public-booking-timeline__item ${
                    done ? 'public-booking-timeline__item--done' : ''
                  } ${active ? 'public-booking-timeline__item--active' : ''} ${
                    clickable ? 'public-booking-timeline__item--clickable' : ''
                  }`}
                  onClick={() => goToStep(step.id)}
                  disabled={!clickable}
                >
                  <span className="public-booking-timeline__dot">
                    {done ? (
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      step.id
                    )}
                  </span>
                  <span className="public-booking-timeline__label">
                    <span className="hidden sm:inline">{step.label}</span>
                    <span className="sm:hidden">{step.short}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {currentStep === 1 && (
            <section className="public-booking-section p-4 sm:p-5">
              <div className="mb-4">
                <h2 className="text-lg font-bold text-slate-900">اختَر الرحلة</h2>
                <p className="text-sm text-slate-500 mt-1">حدّد الرحلة المناسبة لبدء الحجز</p>
              </div>
              {tripsLoading ? (
                <p className="text-sm text-slate-500">جاري تحميل الرحلات...</p>
              ) : trips.length === 0 ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900">
                  لا توجد رحلات منشورة للحجز حالياً.
                </div>
              ) : (
                <ul className="space-y-2.5">
                  {trips.map((trip: any) => {
                    const r = trip.routes?.[0];
                    const active = selectedTripId === trip._id;
                    return (
                      <li key={trip._id}>
                        <button
                          type="button"
                          onClick={() => onSelectTrip(trip._id)}
                          className={`w-full text-right rounded-2xl border px-4 py-4 transition ${
                            active
                              ? 'border-primary-500 bg-primary-50 shadow-sm shadow-primary-100'
                              : 'border-slate-200 bg-white hover:border-primary-200 hover:bg-slate-50/60'
                          }`}
                        >
                          <div className="flex justify-between gap-3 items-start">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-bold text-slate-900">{trip.tripNumber}</p>
                                <span className="text-[11px] rounded-full bg-slate-100 text-slate-600 px-2 py-0.5">
                                  {tripTypeLabel(trip.tripType)}
                                </span>
                              </div>
                              {r && (
                                <p className="text-sm text-slate-600 mt-1.5">
                                  {r.from} ← {r.to}
                                  {r.timeFrom ? ` · ${r.timeFrom}` : ''}
                                </p>
                              )}
                            </div>
                            {trip.date && (
                              <div className="text-xs text-slate-500 shrink-0 rounded-lg bg-white/80 border border-slate-100 px-2 py-1">
                                <DateDisplay value={trip.date} className="inline-flex" />
                              </div>
                            )}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          )}

          {currentStep === 2 && (
            <section className="public-booking-section overflow-hidden">
              <div className="px-4 sm:px-5 py-4 border-b border-slate-100">
                <h2 className="text-lg font-bold text-slate-900">الباص والمسار</h2>
                <p className="text-sm text-slate-500 mt-1">راجع المسار واختر الحافلة</p>
              </div>
              {tripDetailFetching && !selectedTrip?.buses?.length ? (
                <p className="px-4 py-6 text-sm text-slate-500">جاري تحميل تفاصيل الرحلة...</p>
              ) : (
                <div className="p-4 sm:p-5 space-y-5">
                  {route && (
                    <div className="rounded-2xl bg-gradient-to-br from-slate-50 to-primary-50/50 border border-slate-100 p-4">
                      <div className="flex items-center justify-center gap-3 text-center mb-4">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-slate-500 mb-0.5">من</p>
                          <p className="font-semibold text-slate-900 truncate">{route.from}</p>
                        </div>
                        <span className="text-primary-700 shrink-0">←</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-slate-500 mb-0.5">إلى</p>
                          <p className="font-semibold text-slate-900 truncate">{route.to}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-slate-200/80">
                        <div className="text-sm">
                          <p className="text-xs text-slate-500 mb-1">المغادرة</p>
                          <div className="font-medium text-slate-900">
                            <DateDisplay value={route.dateFrom} className="inline-flex" />
                          </div>
                          {route.timeFrom && (
                            <p className="text-slate-600 mt-0.5 text-sm" dir="ltr">
                              {route.timeFrom}
                            </p>
                          )}
                        </div>
                        {(route.dateTo || route.timeTo) && (
                          <div className="text-sm">
                            <p className="text-xs text-slate-500 mb-1">الوصول</p>
                            {route.dateTo && (
                              <div className="font-medium text-slate-900">
                                <DateDisplay value={route.dateTo} className="inline-flex" />
                              </div>
                            )}
                            {route.timeTo && (
                              <p className="text-slate-600 mt-0.5 text-sm" dir="ltr">
                                {route.timeTo}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">
                      اختَر الحافلة <span className="text-red-500">*</span>
                    </p>
                    {buses.length === 0 ? (
                      <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg p-3">
                        لا توجد حافلات مرتبطة بهذه الرحلة.
                      </p>
                    ) : (
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {buses.map((b: any) => {
                          const id = String(b.bus?._id || '');
                          const active =
                            id === String(selectedBusId || selectedBusEntry?.bus?._id || '');
                          const available = b.availableSeats ?? 0;
                          const capacity = b.bus?.capacity || 0;
                          return (
                            <li key={id}>
                              <button
                                type="button"
                                onClick={() => onSelectBus(id)}
                                className={`w-full text-right rounded-2xl border px-4 py-3.5 transition ${
                                  active
                                    ? 'border-primary-500 bg-primary-50'
                                    : 'border-slate-200 bg-white hover:border-primary-200'
                                }`}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    <p className="font-semibold text-slate-900">
                                      {b.bus?.busNumber || 'باص'}
                                    </p>
                                    {b.bus?.plateNumber && (
                                      <p className="text-xs text-slate-500 mt-0.5" dir="ltr">
                                        {b.bus.plateNumber}
                                      </p>
                                    )}
                                  </div>
                                  {active && (
                                    <span className="text-[10px] font-medium text-primary-800 bg-primary-100 px-1.5 py-0.5 rounded">
                                      محدد
                                    </span>
                                  )}
                                </div>
                                <div className="mt-2 flex items-center gap-3 text-xs text-slate-600">
                                  <span>
                                    السعة: <strong className="text-slate-800">{capacity}</strong>
                                  </span>
                                  <span className="text-slate-300">|</span>
                                  <span>
                                    المتاح:{' '}
                                    <strong className={available > 0 ? 'text-primary-700' : 'text-red-600'}>
                                      {available}
                                    </strong>
                                  </span>
                                </div>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </section>
          )}

          {currentStep === 3 && (
            <section className="public-booking-section p-4 sm:p-5 space-y-5">
              <div>
                <h2 className="text-lg font-bold text-slate-900">بيانات المسافرين</h2>
                <p className="text-sm text-slate-500 mt-1">عبّئ بيانات المسؤول والمرافقين إن وُجدوا</p>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-800">الشخص المسؤول</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormField label="الاسم الكامل" required>
                    <Input
                      value={contact.name}
                      onChange={(e) => setContact({ ...contact, name: e.target.value })}
                    />
                  </FormField>
                  <FormField label="رقم الهوية" required>
                    <Input
                      value={contact.idNumber}
                      onChange={(e) => setContact({ ...contact, idNumber: e.target.value })}
                    />
                  </FormField>
                  <FormField label="رقم الجوال" required>
                    <Input
                      type="tel"
                      dir="ltr"
                      value={contact.phone}
                      onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                    />
                  </FormField>
                  <FormField label="الجنسية" required>
                    <Input
                      value={contact.nationality}
                      onChange={(e) => setContact({ ...contact, nationality: e.target.value })}
                    />
                  </FormField>
                  <FormField>
                    <DateInput
                      label={
                        <span>
                          تاريخ الميلاد <span className="text-red-500">*</span>
                        </span>
                      }
                      value={contact.birthDate}
                      onChange={(e) => setContact({ ...contact, birthDate: e.target.value })}
                    />
                  </FormField>
                  <FormField label="الجنس" required>
                    <Select
                      options={genderOptions}
                      value={contact.gender}
                      onChange={(e) =>
                        setContact({
                          ...contact,
                          gender: e.target.value as 'male' | 'female',
                        })
                      }
                    />
                  </FormField>
                  <FormField label="البريد الإلكتروني">
                    <Input
                      type="email"
                      dir="ltr"
                      value={contact.email}
                      onChange={(e) => setContact({ ...contact, email: e.target.value })}
                    />
                  </FormField>
                  <FormField label="الجهة / المنظمة">
                    <Input
                      value={contact.organization}
                      onChange={(e) =>
                        setContact({ ...contact, organization: e.target.value })
                      }
                    />
                  </FormField>
                </div>
              </div>

              <div className="space-y-3 border-t border-slate-100 pt-4">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-slate-800">المرافقون</h3>
                  <button
                    type="button"
                    className="text-sm text-primary-700 hover:underline"
                    onClick={() => setCompanions((prev) => [...prev, emptyCompanion()])}
                  >
                    + إضافة مرافق
                  </button>
                </div>
                {companions.length === 0 && (
                  <p className="text-sm text-slate-500">لا يوجد مرافقون (اختياري)</p>
                )}
                {companions.map((c, index) => (
                  <div
                    key={index}
                    className="border border-slate-100 rounded-xl p-4 space-y-3 bg-slate-50"
                  >
                    <div className="flex justify-between items-center">
                      <p className="text-sm font-medium text-slate-800">مرافق {index + 1}</p>
                      <button
                        type="button"
                        className="text-xs text-red-600"
                        onClick={() => {
                          setCompanions((prev) => prev.filter((_, i) => i !== index));
                          setSelectedSeats((prev) => {
                            const rebuilt: { [k: number]: string } = {};
                            if (prev[0]) rebuilt[0] = prev[0];
                            let ni = 1;
                            companions.forEach((_, i) => {
                              if (i === index) return;
                              if (prev[i + 1]) rebuilt[ni] = prev[i + 1];
                              ni += 1;
                            });
                            return rebuilt;
                          });
                        }}
                      >
                        حذف
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <FormField label="الاسم" required>
                        <Input
                          value={c.name}
                          onChange={(e) => {
                            const v = e.target.value;
                            setCompanions((prev) =>
                              prev.map((row, i) => (i === index ? { ...row, name: v } : row))
                            );
                          }}
                        />
                      </FormField>
                      <FormField label="رقم الهوية" required>
                        <Input
                          value={c.idNumber}
                          onChange={(e) => {
                            const v = e.target.value;
                            setCompanions((prev) =>
                              prev.map((row, i) =>
                                i === index ? { ...row, idNumber: v } : row
                              )
                            );
                          }}
                        />
                      </FormField>
                      <FormField label="الجنسية" required>
                        <Input
                          value={c.nationality}
                          onChange={(e) => {
                            const v = e.target.value;
                            setCompanions((prev) =>
                              prev.map((row, i) =>
                                i === index ? { ...row, nationality: v } : row
                              )
                            );
                          }}
                        />
                      </FormField>
                      <FormField label="الجوال">
                        <Input
                          type="tel"
                          dir="ltr"
                          value={c.phone}
                          onChange={(e) => {
                            const v = e.target.value;
                            setCompanions((prev) =>
                              prev.map((row, i) => (i === index ? { ...row, phone: v } : row))
                            );
                          }}
                        />
                      </FormField>
                      <FormField>
                        <DateInput
                          label={
                            <span>
                              تاريخ الميلاد <span className="text-red-500">*</span>
                            </span>
                          }
                          value={c.birthDate}
                          onChange={(e) => {
                            const v = e.target.value;
                            setCompanions((prev) =>
                              prev.map((row, i) =>
                                i === index ? { ...row, birthDate: v } : row
                              )
                            );
                          }}
                        />
                      </FormField>
                      <FormField label="الجنس" required>
                        <Select
                          options={genderOptions}
                          value={c.gender}
                          onChange={(e) => {
                            const v = e.target.value as 'male' | 'female';
                            setCompanions((prev) =>
                              prev.map((row, i) =>
                                i === index ? { ...row, gender: v } : row
                              )
                            );
                          }}
                        />
                      </FormField>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {currentStep === 4 && (
            <section className="public-booking-section p-4 sm:p-5 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">اختيار المقاعد</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    اضغط على مقعد متاح لتخصيصه للمسافر التالي بدون مقعد.
                  </p>
                </div>
                <span className="text-xs font-semibold text-primary-700 bg-primary-50 rounded-full px-2.5 py-1">
                  {seatsChosen}/{seatsNeeded}
                </span>
              </div>
              {busForSeats && passengersForSeats.some((p) => p.name) ? (
                showSeatSelection && (
                  <BusSeatSelection
                    bus={busForSeats}
                    existingBookings={[]}
                    extraReservedSeats={extraReservedSeats}
                    selectedSeats={selectedSeats}
                    onSeatSelect={handleSeatSelect}
                    passengers={passengersForSeats}
                    isLoadingReserved={tripDetailFetching}
                  />
                )
              ) : (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg p-3">
                  أكمل بيانات المسافرين أولاً ثم عد لاختيار المقاعد.
                </p>
              )}
            </section>
          )}

          {currentStep === 5 && (
            <section className="public-booking-section p-4 sm:p-5 space-y-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900">تأكيد الحجز</h2>
                <p className="text-sm text-slate-500 mt-1">
                  راجع الملخص ثم اضغط «تأكيد الحجز» لإرسال الطلب
                </p>
              </div>

              <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 space-y-3 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-slate-500">الرحلة</span>
                  <span className="font-semibold text-slate-900">
                    {selectedTrip?.tripNumber || '—'}
                    <span className="mr-2 font-normal text-slate-500">
                      {tripTypeLabel(selectedTrip?.tripType)}
                    </span>
                  </span>
                </div>
                {route && (
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-500">المسار</span>
                    <span className="font-medium text-slate-800 text-left">
                      {route.from} ← {route.to}
                    </span>
                  </div>
                )}
                <div className="flex justify-between gap-3">
                  <span className="text-slate-500">الحافلة</span>
                  <span className="font-medium text-slate-800">
                    {selectedBusEntry?.bus?.busNumber || '—'}
                  </span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-slate-500">المسؤول</span>
                  <span className="font-medium text-slate-800">
                    {contact.name.trim() || '—'}
                  </span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-slate-500">الجوال</span>
                  <span className="font-medium text-slate-800" dir="ltr">
                    {contact.phone.trim() || '—'}
                  </span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-slate-500">المسافرون</span>
                  <span className="font-medium text-slate-800">{seatsNeeded}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-slate-500">المقاعد</span>
                  <span className="font-medium text-slate-800" dir="ltr">
                    {Object.values(selectedSeats).join(' · ') || '—'}
                  </span>
                </div>
              </div>

              <FormField label="ملاحظات / طلبات خاصة (اختياري)">
                <textarea
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-primary-600/20 focus:border-primary-600"
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value)}
                  placeholder="أي طلب خاص... (اختياري)"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                    }
                  }}
                />
              </FormField>

              <button
                type="button"
                disabled={!confirmReady || createMutation.isPending}
                onClick={confirmBooking}
                className="w-full rounded-xl bg-primary-700 py-3.5 text-base font-bold text-white hover:bg-primary-800 disabled:opacity-60 shadow-lg shadow-primary-700/20"
              >
                {createMutation.isPending
                  ? 'جاري تأكيد الحجز...'
                  : !confirmReady
                    ? 'راجع الملخص والملاحظات...'
                    : 'تأكيد الحجز'}
              </button>
              <p className="text-center text-xs text-slate-500 pb-2">
                يمكنك كتابة ملاحظات اختيارية ثم الضغط على تأكيد الحجز
              </p>
            </section>
          )}

          {formError && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl p-3">
              {formError}
            </p>
          )}

          {(currentStep > 1 || selectedTripId) && (
            <div className="fixed bottom-0 inset-x-0 z-20 border-t border-slate-200/80 bg-white/95 backdrop-blur px-4 py-3">
              <div className="max-w-3xl mx-auto">
                <div className="public-booking-nav">
                  {currentStep > 1 ? (
                    <button
                      type="button"
                      className="public-booking-nav__btn public-booking-nav__btn--ghost"
                      onClick={goBack}
                    >
                      السابق
                    </button>
                  ) : (
                    <div className="flex-1" />
                  )}
                  {currentStep < 5 ? (
                    <button
                      type="button"
                      className="public-booking-nav__btn public-booking-nav__btn--primary"
                      onClick={goNext}
                      disabled={currentStep === 1 && !selectedTripId}
                    >
                      التالي
                    </button>
                  ) : (
                    <div className="flex-1" />
                  )}
                </div>
                {currentStep === 5 && (
                  <p className="mt-2 text-center text-xs text-slate-500">
                    اكتب ملاحظاتك إن رغبت، ثم اضغط تأكيد الحجز أعلاه
                  </p>
                )}
              </div>
            </div>
          )}
        </form>
      </main>
    </div>
  );
};

export default PublicBookingPage;
